import type { User } from 'firebase/auth';
import { webConfig } from '@/config/env';
import { IS_DEMO } from '@/demo/demoMode';
import { adminApi } from './adminApi';
import { getFirebase } from './firebase';

/**
 * התראות Push למנהלים (Firebase Cloud Messaging).
 * - אנדרואיד/מחשב: עובד בכל דפדפן תומך.
 * - אייפון: רק מהאפליקציה המותקנת במסך הבית, iOS 16.4 ומעלה.
 * הטוקן נרשם בשרת לחשבון המנהל; השרת שולח לכל המכשירים של שאר המנהלים.
 */

export type PushState = 'demo' | 'unsupported' | 'ios_needs_install' | 'not_configured' | 'denied' | 'off' | 'on';

const STORAGE_KEY = 'sa-push-token';

function savedToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function currentPushState(): PushState {
  if (IS_DEMO) return 'demo';
  if (isIos() && !isStandalone()) return 'ios_needs_install';
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (!webConfig.ok || !webConfig.config.vapidKey) return 'not_configured';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.permission === 'granted' && savedToken() ? 'on' : 'off';
}

export async function enablePush(user: User): Promise<PushState> {
  const state = currentPushState();
  if (state !== 'off' && state !== 'on') return state;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
  if (!(await isSupported())) return 'unsupported';
  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(getMessaging(getFirebase().app), {
    vapidKey: webConfig.ok ? webConfig.config.vapidKey ?? undefined : undefined,
    serviceWorkerRegistration: registration,
  });
  if (!token) return 'off';
  await adminApi(user).registerDevice(token);
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* אחסון חסום — ההתראות עדיין פועלות */
  }
  return 'on';
}

export async function disablePush(user: User): Promise<PushState> {
  const token = savedToken();
  if (token) {
    await adminApi(user).unregisterDevice(token).catch(() => undefined);
    try {
      const { getMessaging, deleteToken } = await import('firebase/messaging');
      await deleteToken(getMessaging(getFirebase().app));
    } catch {
      /* לא קריטי */
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  return currentPushState();
}

export const PUSH_STATE_TEXT: Record<PushState, string> = {
  demo: 'במצב תצוגה אין התראות Push. אחרי חיבור Firebase אפשר יהיה להפעיל אותן כאן.',
  unsupported: 'הדפדפן הזה לא תומך בהתראות Push.',
  ios_needs_install: 'באייפון ההתראות עובדות רק מהאפליקציה המותקנת: שיתוף ← "הוספה למסך הבית", ואז לפתוח משם.',
  not_configured: 'התראות Push עוד לא הוגדרו (חסר מפתח VAPID ב-.env.local).',
  denied: 'ההתראות נחסמו בדפדפן. אפשר לאפשר אותן בהגדרות האתר בדפדפן.',
  off: 'קבלו התראה לטלפון כשנרשם מישהו חדש, כשמגיעה בקשת שינוי וכשמנהל אחר משנה עבודה.',
  on: 'ההתראות פעילות במכשיר הזה.',
};
